import axiosInstance from "./apiinterseptor"; // Corrected import path

export interface ComplaintData {
  title: string;
  description: string;
  category: string;
  priority: string;
  screenshots?: string[];
  attachments?: string[];
}

export interface Complaint {
  _id: string;
  ticketId: string;
  title: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  screenshots: string[];
  attachments: string[];
  resolution?: string;
  raisedBy: {
    _id: string;
    name: string;
    email: string;
  };
  assignedTo?: {
    _id: string;
    name: string;
    email: string;
  };
  resolvedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface ComplaintListResponse {
  success: boolean;
  data: {
    complaints: Complaint[];
    pagination: {
      page: number;
      limit: number;
      totalPages: number;
      totalResults: number;
    };
  };
}

export interface ComplaintResponse {
  success: boolean;
  data: {
    complaint: Complaint;
  };
  message?: string;
}

export interface ComplaintStats {
  success: boolean;
  data: {
    total: number;
    open: number;
    in_progress: number;
    resolved: number;
    closed: number;
  };
}

// Create a new complaint
export const createComplaint = async (complaintData: ComplaintData): Promise<ComplaintResponse> => {
  try {
    const response = await axiosInstance.post('/complaints', complaintData);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create complaint');
  }
};

// Get user's complaints
export const getUserComplaints = async (
  page: number = 1,
  limit: number = 10,
  search?: string
): Promise<ComplaintListResponse> => {
  try {
    const response = await axiosInstance.get('/complaints/my-complaints', {
      params: {
        page,
        limit,
        search
      }
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch complaints');
  }
};

// Get all complaints (admin only)
export const getAllComplaints = async (
  page: number = 1,
  limit: number = 10,
  status?: string,
  priority?: string,
  category?: string,
  search?: string
): Promise<ComplaintListResponse> => {
  try {
    const response = await axiosInstance.get('/complaints/all', {
      params: {
        page,
        limit,
        status,
        priority,
        category,
        search
      }
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch all complaints');
  }
};

// Get complaint by ID
export const getComplaintById = async (complaintId: string): Promise<ComplaintResponse> => {
  try {
    const response = await axiosInstance.get(`/complaints/${complaintId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch complaint');
  }
};

// Update complaint status (admin only)
export const updateComplaintStatus = async (
  complaintId: string,
  status: string,
  resolution?: string,
  assignedTo?: string
): Promise<ComplaintResponse> => {
  try {
    const response = await axiosInstance.patch(`/complaints/${complaintId}/status`, {
      status,
      resolution,
      assignedTo
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update complaint status');
  }
};

// Get complaint statistics (admin only)
export const getComplaintStats = async (): Promise<ComplaintStats> => {
  try {
    const response = await axiosInstance.get('/complaints/stats');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch complaint statistics');
  }
};

// Get pending complaints count (admin only)
export const getPendingComplaintsCount = async (): Promise<{ success: boolean; data: { count: number } }> => {
  try {
    const response = await axiosInstance.get('/complaints/pending-count');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch pending complaints count');
  }
};

// Upload file for complaint
export const uploadComplaintFile = async (file: File, folderPath: string = 'complaints/screenshots'): Promise<{ success: boolean; data: { url: string } }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderPath', folderPath);

    const response = await axiosInstance.post('/uploads/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    // Transform backend response to match frontend expectations
    return {
      success: response.data.status === 'success',
      data: {
        url: response.data.data.fileUrl
      }
    };
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to upload file');
  }
};
